<template>
  <div class="min-h-screen bg-gradient-to-br from-[#FAFBFF] via-[#F8FAFF] to-[#F0F7FF] dark:from-[#0a0a0a] dark:to-[#1a1a1a] p-6" :dir="isRtl ? 'rtl' : 'ltr'">
    <div class="max-w-7xl mx-auto">
      <!-- Main Card -->
      <div class="bg-white/80 dark:bg-[#1a1a1a]/90 backdrop-blur-xl rounded-2xl shadow-lg border border-white/20 dark:border-[#374151]/50 overflow-hidden">
        
        <!-- Header -->
        <div class="bg-gradient-to-r from-[#667eea] to-[#764ba2] p-6">
          <div class="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <!-- Title & Search -->
            <div class="flex-1 w-full sm:w-auto">
              <div class="flex items-center gap-4 mb-4">
                <div class="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                  <i class="material-symbols-outlined text-white text-xl">inventory_2</i>
                </div>
                <div>
                  <h1 class="text-xl font-bold text-white">{{ directT('ecommerce.productsList.title') }}</h1>
                  <p class="text-white/80 text-sm">{{ directT('ecommerce.productsList.subtitle') }}</p>
                </div>
              </div>
              
              <!-- Search Bar -->
              <form class="relative max-w-md" @submit.prevent="fetchProducts">
                <div class="absolute left-4 top-1/2 -translate-y-1/2 text-white/80">
                  <i class="material-symbols-outlined text-lg">search</i>
                </div>
                <input
                  type="text"
                  v-model="searchQuery"
                  @input="fetchProducts"
                  :placeholder="directT('ecommerce.productsList.searchHere')"
                  class="w-full h-12 rounded-xl bg-white/20 border border-white/30 text-white placeholder-white/70 pl-12 pr-4 text-sm outline-none focus:border-white focus:ring-2 focus:ring-white/20 transition-all duration-300 backdrop-blur-sm"
                />
              </form>
            </div>

            <!-- Add Product Button -->
            <div class="w-full sm:w-auto">
              <button
                @click="navigateToCreateProduct"
                class="group w-full sm:w-auto inline-flex items-center justify-center gap-3 px-6 py-3 bg-white/20 text-white font-semibold rounded-xl hover:bg-white/30 transition-all duration-300 backdrop-blur-sm border border-white/30 hover:border-white/50 transform hover:scale-105"
              >
                <div class="w-5 h-5 rounded-lg bg-white/20 flex items-center justify-center">
                  <i class="material-symbols-outlined text-sm">add</i>
                </div>
                <span>{{ directT('ecommerce.productsList.addNewProduct') }}</span>
                <i class="material-symbols-outlined text-sm group-hover:translate-x-1 transition-transform duration-300">arrow_forward</i>
              </button>
            </div>
          </div>
        </div>

        <!-- Content -->
        <div class="p-6">
          <!-- Loading State -->
          <div v-if="loading" class="flex flex-col items-center justify-center py-20">
            <div class="relative">
              <div class="w-12 h-12 border-4 border-[#667eea]/20 border-t-[#667eea] rounded-full animate-spin"></div>
              <div class="absolute inset-0 w-12 h-12 border-4 border-transparent border-r-[#764ba2] rounded-full animate-spin" style="animation-delay: -0.5s;"></div>
            </div>
            <p class="mt-4 text-base font-medium text-[#667eea]">{{ directT('ecommerce.productsList.loadingProducts') }}</p>
          </div>

          <!-- Error State -->
          <div v-else-if="error" class="bg-red-50/80 dark:bg-red-900/20 backdrop-blur-xl rounded-2xl shadow-lg border border-red-200/50 dark:border-red-800/30 p-6 mb-6">
            <div class="flex items-start gap-4">
              <div class="w-10 h-10 bg-red-500/10 rounded-xl flex items-center justify-center">
                <i class="material-symbols-outlined text-red-500 text-xl">error</i>
              </div>
              <div>
                <h3 class="text-lg font-semibold text-red-700 dark:text-red-400">{{ directT('ecommerce.productsList.errorLoadingProducts') }}</h3>
                <p class="text-red-600 dark:text-red-300 mt-1">{{ error }}</p>
              </div>
            </div>
          </div>

          <!-- Empty State -->
          <div v-else-if="products.length === 0" class="text-center py-16">
            <div class="w-16 h-16 bg-gradient-to-r from-gray-100 to-gray-200 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <i class="material-symbols-outlined text-gray-400 text-2xl">inventory_2</i>
            </div>
            <h3 class="text-lg font-semibold text-gray-700 mb-2">{{ directT('ecommerce.productsList.noProductsFound') }}</h3>
            <p class="text-gray-500">{{ directT('ecommerce.productsList.noProductsFound') }}</p>
          </div>

          <!-- Products Table -->
          <div v-else class="overflow-hidden rounded-2xl border border-gray-200/50 dark:border-gray-700/50">
            <div class="overflow-x-auto">
              <table class="w-full">
                <!-- Table Header -->
                <thead>
                  <tr class="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-[#1e293b] dark:to-[#334155]">
                    <th class="ltr:text-left rtl:text-right px-6 py-4 font-semibold text-gray-700 dark:text-gray-200 text-sm ltr:first:rounded-tl-md ltr:last:rounded-tr-md rtl:first:rounded-tr-md rtl:last:rounded-tl-md">
                      {{ directT('ecommerce.productsList.image') }}
                    </th>
                    <th class="ltr:text-left rtl:text-right px-6 py-4 font-semibold text-gray-700 dark:text-gray-200 text-sm">
                      {{ directT('ecommerce.productsList.product') }}
                    </th>
                    <th class="ltr:text-left rtl:text-right px-6 py-4 font-semibold text-gray-700 dark:text-gray-200 text-sm">
                      {{ directT('ecommerce.productsList.category') }}
                    </th>
                    <th class="ltr:text-left rtl:text-right px-6 py-4 font-semibold text-gray-700 dark:text-gray-200 text-sm">
                      {{ directT('ecommerce.productsList.price') }}
                    </th>
                    <th class="ltr:text-left rtl:text-right px-6 py-4 font-semibold text-gray-700 dark:text-gray-200 text-sm">
                      {{ directT('ecommerce.productsList.amount') }}
                    </th>
                    <th class="ltr:text-left rtl:text-right px-6 py-4 font-semibold text-gray-700 dark:text-gray-200 text-sm ltr:first:rounded-tl-md ltr:last:rounded-tr-md rtl:first:rounded-tr-md rtl:last:rounded-tl-md">
                      {{ directT('ecommerce.productsList.actions') }}
                    </th>
                  </tr>
                </thead>

                <!-- Table Body -->
                <tbody class="bg-white dark:bg-[#1a1a1a] divide-y divide-gray-100 dark:divide-gray-700/50">
                  <tr 
                    v-for="product in products" 
                    :key="product.id"
                    class="hover:bg-gradient-to-r hover:from-[#667eea]/5 hover:to-[#764ba2]/5 transition-all duration-300 group"
                  >
                    <!-- Product Image -->
                    <td class="px-6 py-4 ltr:text-left rtl:text-right ltr:first:border-l ltr:last:border-r rtl:first:border-r rtl:last:border-l">
                      <div class="flex ltr:justify-start rtl:justify-end">
                        <div class="w-12 h-12 rounded-xl overflow-hidden border-2 border-white shadow-md">
                          <img
                            :src="product.image_link || require('@/assets/images/products/product1.jpg')"
                            class="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                            alt="product-image"
                          />
                        </div>
                      </div>
                    </td>

                    <!-- Product Name & Info -->
                    <td class="px-6 py-4 ltr:text-left rtl:text-right">
                      <div class="ltr:text-left rtl:text-right">
                        <RouterLink
                          :to="`/ecommerce/edit-product?id=${product.id}`"
                          class="font-semibold text-gray-900 dark:text-white hover:text-[#667eea] transition-colors duration-200 text-sm block mb-1"
                        >
                          {{ product.title }}
                        </RouterLink>
                        <span class="text-xs text-gray-500 dark:text-gray-400 block">
                          {{ product.published_at ? new Date(product.published_at).toLocaleDateString() : directT('ecommerce.productsList.draft') }}
                        </span>
                      </div>
                    </td>

                    <!-- Category -->
                    <td class="px-6 py-4 ltr:text-left rtl:text-right">
                      <div class="flex ltr:justify-start rtl:justify-end">
                        <span class="inline-flex items-center px-3 py-1 rounded-full bg-gradient-to-r from-blue-50 to-blue-100 text-blue-600 text-xs font-medium border border-blue-200">
                          {{ product.category || directT('ecommerce.productsList.uncategorized') }}
                        </span>
                      </div>
                    </td>

                    <!-- Price -->
                    <td class="px-6 py-4 ltr:text-left rtl:text-right">
                      <div class="font-semibold text-gray-900 dark:text-white text-sm">
                        {{ formatPrice(product.price) }}
                      </div>
                    </td>

                    <!-- Amount (Compare Price) -->
                    <td class="px-6 py-4 ltr:text-left rtl:text-right">
                      <div class="font-semibold text-gray-900 dark:text-white text-sm">
                        {{ formatPrice(product.compare_at_price) }}
                      </div>
                    </td>

                    <!-- Actions -->
                    <td class="px-6 py-4 ltr:first:border-l ltr:last:border-r rtl:first:border-r rtl:last:border-l">
                      <div class="flex items-center ltr:justify-start rtl:justify-end gap-3">
                        <!-- Edit Button -->
                        <RouterLink
                          :to="`/ecommerce/edit-product?id=${product.id}`"
                          class="group relative w-10 h-10 bg-gradient-to-br from-[#667eea] to-[#764ba2] rounded-xl flex items-center justify-center text-white shadow-lg hover:shadow-xl transform hover:scale-110 transition-all duration-300 hover:-translate-y-1"
                        >
                          <i class="material-symbols-outlined text-base font-medium">edit_note</i>
                          <div class="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                        </RouterLink>

                        <!-- View Button -->
                        <button
                          class="group relative w-10 h-10 bg-gradient-to-br from-[#4facfe] to-[#00f2fe] rounded-xl flex items-center justify-center text-white shadow-lg hover:shadow-xl transform hover:scale-110 transition-all duration-300 hover:-translate-y-1"
                        >
                          <i class="material-symbols-outlined text-base font-medium">visibility</i>
                          <div class="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                        </button>

                        <!-- Delete Button -->
                        <button
                          type="button"
                          class="group relative w-10 h-10 bg-gradient-to-br from-[#ff6b6b] to-[#ee5a52] rounded-xl flex items-center justify-center text-white shadow-lg hover:shadow-xl transform hover:scale-110 transition-all duration-300 hover:-translate-y-1"
                        >
                          <i class="material-symbols-outlined text-base font-medium">delete_forever</i>
                          <div class="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                        </button>

                        <!-- Duplicate Button -->
                        <button
                          class="group relative w-10 h-10 bg-gradient-to-br from-[#a8edea] to-[#fed6e3] rounded-xl flex items-center justify-center text-gray-600 shadow-lg hover:shadow-xl transform hover:scale-110 transition-all duration-300 hover:-translate-y-1"
                        >
                          <i class="material-symbols-outlined text-base font-medium">content_copy</i>
                          <div class="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Footer Stats -->
          <div v-if="products.length > 0" class="mt-6 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-[#1e293b] dark:to-[#334155] rounded-2xl p-4">
            <div class="flex items-center justify-between text-sm">
              <span class="text-gray-600 dark:text-gray-300">
                {{ directT('ecommerce.productsList.showingProducts', { count: products.length }) }}
              </span>
              <div class="flex items-center gap-4">
                <span class="text-gray-600 dark:text-gray-300">{{ directT('ecommerce.productsList.totalProducts') }}</span>
                <span class="inline-flex items-center px-3 py-1 rounded-full bg-gradient-to-r from-[#667eea] to-[#764ba2] text-white text-xs font-semibold">
                  {{ products.length }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, computed } from "vue";
import { formatCurrency, getUserCurrency } from "@/utils/currency";
import { supabase } from "@/lib/supabaseClient";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import enTranslations from "@/i18n/locales/en.json";
import heTranslations from "@/i18n/locales/he.json";
import arTranslations from "@/i18n/locales/ar.json";

interface Product {
  id: string;
  title: string;
  published_at: string;
  category: string;
  price: number;
  compare_at_price: number;
  image_link: string;
}

// Type definition for translation objects
type TranslationObject = {
  [key: string]: string | TranslationObject;
};

export default defineComponent({
  name: "ProductsList",
  setup() {
    const router = useRouter();
    const activeTab = ref("allProducts");
    const currencyCode = ref('ILS'); // ברירת מחדל
    const products = ref<Product[]>([]);
    const loading = ref(true);
    const error = ref<string | null>(null);
    const searchQuery = ref("");
    const organization = ref<any>(null);

    // i18n setup
    const { locale } = useI18n();
    
    // Dynamic direction based on language
    const isRtl = computed(() => ['he', 'ar'].includes(locale.value));

    // Translation files
    const translationFiles = {
      en: enTranslations,
      he: heTranslations,
      ar: arTranslations
    };

    // Direct translation function that bypasses vue-i18n
    const directT = (key: string, params?: Record<string, any>): string => {
      const parts = key.split('.');
      let result: TranslationObject = translationFiles[locale.value as keyof typeof translationFiles];
      
      for (const part of parts) {
        if (!result || !(part in result)) {
          console.warn(`Missing translation key: ${key} in ${locale.value} locale`);
          // Fallback to English if translation not found
          let enResult: TranslationObject = translationFiles.en;
          for (const fallbackPart of parts) {
            if (!enResult || !(fallbackPart in enResult)) return key;
            enResult = enResult[fallbackPart as keyof typeof enResult] as TranslationObject;
          }
          return typeof enResult === 'string' ? enResult : key;
        }
        result = result[part as keyof typeof result] as TranslationObject;
      }
      
      let translation = typeof result === 'string' ? result : key;
      
      // Replace parameters if provided
      if (params) {
        Object.keys(params).forEach(paramKey => {
          translation = translation.replace(new RegExp(`\\{${paramKey}\\}`, 'g'), String(params[paramKey]));
        });
      }
      
      return translation;
    };

    // Computed property to check if user can add products
    const canAddProducts = computed(() => {
      return organization.value?.ecommerce === true;
    });

    // Fetch organization data for the current user
    const fetchOrganization = async () => {
      try {
        // Get current user from Supabase auth
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        // Get the user's organization
        const { data: userData, error: userError } = await supabase
          .from('user_profiles')
          .select('organization_id')
          .eq('user_id', user.id)
          .single();

        if (userError || !userData?.organization_id) {
          console.error('Error fetching user organization:', userError);
          return;
        }

        // Get organization details
        const { data: orgData, error: orgError } = await supabase
          .from('organizations')
          .select('*')
          .eq('id', userData.organization_id)
          .single();

        if (orgError) {
          console.error('Error fetching organization:', orgError);
          return;
        }

        organization.value = orgData;
      } catch (err) {
        console.error('Unexpected error fetching organization:', err);
      }
    };

    // Load currency code and products when component mounts
    onMounted(async () => {
      try {
        // Get user's currency
        currencyCode.value = await getUserCurrency();
        await fetchOrganization();
        fetchProducts();
      } catch (error) {
        console.error('Error during initialization:', error);
      }
    });

    const fetchProducts = async () => {
      try {
        loading.value = true;
        error.value = null;

        let query = supabase.from("products").select("*");

        // Filter by search query if provided
        if (searchQuery.value) {
          query = query.ilike("title", `%${searchQuery.value}%`);
        }

        const { data, error: fetchError } = await query;

        if (fetchError) {
          throw fetchError;
        }

        // Cast the data to the Product type
        products.value = (data || []).map(item => ({
          id: String(item.id || ''),
          title: String(item.title || ''),
          published_at: String(item.published_at || ''),
          category: String(item.category || ''),
          price: typeof item.price === 'number' ? item.price : 0,
          compare_at_price: typeof item.compare_at_price === 'number' ? item.compare_at_price : 0,
          image_link: String(item.image_link || '')
        }));
        
        loading.value = false;
      } catch (err: any) {
        console.error("Error loading products:", err);
        error.value = directT('ecommerce.productsList.errorLoadingProducts') + `: ${err.message || directT('ecommerce.productsList.unknownError')}`;
        loading.value = false;
      }
    };

    const formatPrice = (price: number | null) => {
      if (!price) return "₪0.00";
      return formatCurrency(price, currencyCode.value);
    };

    const navigateToCreateProduct = () => {
      router.push('/ecommerce/create-product');
    };

    return {
      products,
      loading,
      error,
      searchQuery,
      formatPrice,
      fetchProducts,
      navigateToCreateProduct,
      canAddProducts,
      directT,
      isRtl
    };
  },
});
</script>
